import { jwtDecode } from 'jwt-decode';

const AUTH_KEY = 'auth';
const ACCESS_TOKEN_KEY = 'token'; // legacy key used across the app
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_TYPE_KEY = 'token_type';
const EXPIRES_AT_KEY = 'token_expires_at';
const LAST_REFRESH_AT_KEY = 'last_refresh_at';
const USER_PROFILE_KEY = 'auth_user';
const LEGACY_USER_PROFILE_KEY = 'user';
const USERNAME_KEY = 'username';
const USER_ID_KEY = 'user_id';

const CLOCK_SKEW_MS = 30 * 1000;
const API_BASE = process.env.REACT_APP_API_URL_BASE;

const readJson = async (response) => {
    try {
        return await response.json();
    } catch (error) {
        return {};
    }
};

const normalizeScopes = (scopesValue) => {
    if (Array.isArray(scopesValue)) {
        return scopesValue;
    }

    if (typeof scopesValue === 'string') {
        return scopesValue
            .split(',')
            .map((scope) => scope.trim())
            .filter(Boolean);
    }

    return [];
};

const resolveUserId = (profile) => {
    if (!profile || typeof profile !== 'object') {
        return null;
    }

    return profile.user_id ?? profile.web_user_id ?? profile.id ?? null;
};

const normalizeUserProfile = (profilePayload) => {
    if (!profilePayload || typeof profilePayload !== 'object') {
        return {
            scopes: [],
        };
    }

    const nestedUser = profilePayload?.user;
    const baseUser = nestedUser && typeof nestedUser === 'object' ? nestedUser : profilePayload;
    const scopes = normalizeScopes(baseUser?.scopes ?? profilePayload?.scopes);

    const normalized = {
        ...profilePayload,
        ...baseUser,
        scopes,
    };

    if (normalized.customer === undefined && normalized.customer_id !== undefined) {
        normalized.customer = normalized.customer_id;
    }

    if (normalized.user_type === undefined && normalized.user_type_id !== undefined) {
        normalized.user_type = normalized.user_type_id;
    }

    return normalized;
};

export const clearAuthSession = () => {
    window.sessionStorage.removeItem(AUTH_KEY);
    window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    window.sessionStorage.removeItem(TOKEN_TYPE_KEY);
    window.sessionStorage.removeItem(EXPIRES_AT_KEY);
    window.sessionStorage.removeItem(LAST_REFRESH_AT_KEY);
    window.sessionStorage.removeItem(USER_PROFILE_KEY);
    window.sessionStorage.removeItem(LEGACY_USER_PROFILE_KEY);
    window.sessionStorage.removeItem(USERNAME_KEY);
    window.sessionStorage.removeItem(USER_ID_KEY);
};

export const saveAuthSession = ({ access_token, refresh_token, token_type = 'Bearer', expires_in }) => {
    if (!access_token) {
        throw new Error('Missing access token');
    }

    window.sessionStorage.setItem(AUTH_KEY, 'true');
    window.sessionStorage.setItem(ACCESS_TOKEN_KEY, access_token);
    window.sessionStorage.setItem(TOKEN_TYPE_KEY, token_type || 'Bearer');

    if (refresh_token) {
        window.sessionStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    }

    if (typeof expires_in === 'number') {
        window.sessionStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + (expires_in * 1000)));
    }

    // Track the last token update time so heartbeat validation can cap refresh traffic.
    window.sessionStorage.setItem(LAST_REFRESH_AT_KEY, String(Date.now()));
};

export const isAuthenticated = () => Boolean(window.sessionStorage.getItem(AUTH_KEY) && window.sessionStorage.getItem(ACCESS_TOKEN_KEY));

export const getAccessToken = () => window.sessionStorage.getItem(ACCESS_TOKEN_KEY) || '';

export const getLastRefreshAt = () => {
    const stored = Number(window.sessionStorage.getItem(LAST_REFRESH_AT_KEY));
    if (Number.isNaN(stored) || stored <= 0) {
        return 0;
    }

    return stored;
};

const getTokenType = () => window.sessionStorage.getItem(TOKEN_TYPE_KEY) || 'Bearer';

const buildAuthHeader = (token) => {
    const tokenType = getTokenType();
    return {
        Authorization: `${tokenType} ${token}`,
    };
};

const getExpiresAt = () => {
    const stored = Number(window.sessionStorage.getItem(EXPIRES_AT_KEY));
    if (!Number.isNaN(stored) && stored > 0) {
        return stored;
    }

    const token = getAccessToken();
    if (!token) {
        return 0;
    }

    try {
        const decoded = jwtDecode(token);
        if (decoded?.exp) {
            const derived = Number(decoded.exp) * 1000;
            window.sessionStorage.setItem(EXPIRES_AT_KEY, String(derived));
            return derived;
        }
    } catch (error) {
        return 0;
    }

    return 0;
};

export const isTokenExpired = () => {
    const expiresAt = getExpiresAt();
    if (!expiresAt) {
        return false;
    }

    return (Date.now() + CLOCK_SKEW_MS) >= expiresAt;
};

export const getCurrentUser = () => {
    const raw = window.sessionStorage.getItem(USER_PROFILE_KEY) || window.sessionStorage.getItem(LEGACY_USER_PROFILE_KEY);
    if (raw) {
        try {
            const parsed = JSON.parse(raw);
            return normalizeUserProfile(parsed);
        } catch (error) {
            // fall through to token decode fallback
        }
    }

    const token = getAccessToken();
    if (!token) {
        return null;
    }

    try {
        const decoded = jwtDecode(token);
        return normalizeUserProfile(decoded);
    } catch (error) {
        return null;
    }
};

export const hasScope = (scope) => {
    const user = getCurrentUser();
    return normalizeScopes(user?.scopes).includes(scope);
};

export const refreshAccessToken = async () => {
    const refreshToken = window.sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE}/auth/refresh/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
        clearAuthSession();
        throw new Error('Session refresh failed');
    }

    const data = await readJson(response);
    saveAuthSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token || refreshToken,
        token_type: data.token_type || 'Bearer',
        expires_in: data.expires_in,
    });

    return data.access_token;
};

export const getValidAccessToken = async () => {
    const token = getAccessToken();
    if (!token) {
        return '';
    }

    if (isTokenExpired()) {
        return refreshAccessToken();
    }

    return token;
};

export const fetchCurrentUserProfile = async () => {
    let token = await getValidAccessToken();
    if (!token) {
        throw new Error('No access token available');
    }

    try {
        let response = await fetch(`${API_BASE}/auth/me/`, {
            method: 'GET',
            headers: buildAuthHeader(token),
        });

        if (response.status === 401) {
            token = await refreshAccessToken();

            response = await fetch(`${API_BASE}/auth/me/`, {
                method: 'GET',
                headers: buildAuthHeader(token),
            });
        }

        if (!response.ok) {
            throw new Error('Unable to load current user profile');
        }

        const profile = await readJson(response);
        const normalizedProfile = normalizeUserProfile(profile);

        window.sessionStorage.setItem(USER_PROFILE_KEY, JSON.stringify(normalizedProfile));
        window.sessionStorage.setItem(LEGACY_USER_PROFILE_KEY, JSON.stringify(normalizedProfile));

        if (normalizedProfile?.username) {
            window.sessionStorage.setItem(USERNAME_KEY, normalizedProfile.username);
        }

        const userId = resolveUserId(normalizedProfile);
        if (userId !== null && userId !== undefined) {
            window.sessionStorage.setItem(USER_ID_KEY, String(userId));
        }

        return normalizedProfile;
    } catch (error) {
        console.error('[auth] fetchCurrentUserProfile error', error);

        // If /auth/me is temporarily unreachable, keep sign-in/session alive using token claims.
        const decoded = getCurrentUser();
        if (!decoded) {
            throw error;
        }

        const fallbackProfile = normalizeUserProfile(decoded);
        window.sessionStorage.setItem(USER_PROFILE_KEY, JSON.stringify(fallbackProfile));
        window.sessionStorage.setItem(LEGACY_USER_PROFILE_KEY, JSON.stringify(fallbackProfile));

        if (fallbackProfile?.username) {
            window.sessionStorage.setItem(USERNAME_KEY, fallbackProfile.username);
        }

        return fallbackProfile;
    }
};

export const loginWithPassword = async ({ username, password }) => {
    const response = await fetch(`${API_BASE}/auth/login/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
    });

    const data = await readJson(response);

    if (!response.ok) {
        const message = data?.detail || data?.message || response.statusText || 'Sign in failed';
        throw new Error(message);
    }

    saveAuthSession(data);
    await fetchCurrentUserProfile();

    return data;
};

export const initializeAuth = async () => {
    const hasAccess = Boolean(getAccessToken());
    const hasRefresh = Boolean(window.sessionStorage.getItem(REFRESH_TOKEN_KEY));

    if (!hasAccess && !hasRefresh) {
        clearAuthSession();
        return false;
    }

    try {
        if (!hasAccess || isTokenExpired()) {
            await refreshAccessToken();
        }

        await fetchCurrentUserProfile();
        window.sessionStorage.setItem(AUTH_KEY, 'true');
        return true;
    } catch (error) {
        clearAuthSession();
        return false;
    }
};

export const refreshIfNeeded = async () => {
    if (!isAuthenticated()) {
        return false;
    }

    if (!isTokenExpired()) {
        return true;
    }

    try {
        await refreshAccessToken();
        await fetchCurrentUserProfile();
        return true;
    } catch (error) {
        return false;
    }
};

export const validateSessionHeartbeat = async () => {
    if (!isAuthenticated()) {
        return false;
    }

    try {
        await refreshAccessToken();
        return true;
    } catch (error) {
        return false;
    }
};
